import React from 'react';
import { Show, EditButton, ListButton, useRecordContext } from 'react-admin';
import {
    Card,
    CardContent,
    Grid,
    Typography,
    makeStyles,
    Divider,
    Box,
} from '@material-ui/core';
import moment from 'moment';
import jwt_decode from 'jwt-decode';
import PersonIcon from '@material-ui/icons/Person';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import GestureIcon from '@material-ui/icons/Gesture';
import ErrorBoundary from '../ErrorBoundary';
import DataList from '../controls/DataList';
import ImageWithFallback from '../controls/ImageWithFallback';

const useStyles = makeStyles((theme) => ({
    root: {
        // The react-admin layout already insets the page; a second full
        // spacing(3) gutter here only narrowed the usable content area.
        padding: theme.spacing(1, 0, 3),
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
    },
    card: {
        marginBottom: theme.spacing(3),
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)', // Subtle shadow for a professional look
        borderRadius: theme.spacing(1), // Add border-radius for modern look
    },
    // Section cards hold tables that must be free to scroll internally.
    sectionContent: {
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        padding: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    activityGridItem: {
        minWidth: 0,
        maxWidth: '100%',
    },
    showCard: {
        padding: theme.spacing(2),
        minWidth: 0,
        maxWidth: '100%',
        overflowX: 'hidden',
    },
    cardHeader: {
        paddingBottom: theme.spacing(1),
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center', // Center image
        marginBottom: theme.spacing(2),
    },
    image: {
        maxWidth: '100%', // Responsive image
        maxHeight: '250px', // Limit image height
        borderRadius: theme.spacing(1),
        boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)', // Add shadow to image
    },
    sectionTitle: {
        marginBottom: theme.spacing(2),
        fontWeight: 500, // Use a medium font weight
        color: theme.palette.primary.main, // Use primary color
    },
    fieldLabel: {
        fontWeight: 'bold',
        marginRight: theme.spacing(0.5),
        color: theme.palette.text.secondary, // Use secondary text color
    },
    printButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        padding: theme.spacing(1, 2),
        borderRadius: theme.spacing(0.5),
        marginBottom: theme.spacing(2),
    },
    sign: {
        maxWidth: '150px', // Increased size
        maxHeight: 'auto',
        borderRadius: theme.spacing(0.5),
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)', // Add shadow to signature
    },
    // Placeholders mirror the footprint of the image each one replaces so the
    // layout does not shift when media is missing or fails to load.
    photoPlaceholder: {
        width: '100%',
        maxWidth: '250px',
        height: '200px',
    },
    signPlaceholder: {
        display: 'inline-flex',
        width: '150px',
        minHeight: 60,
        marginLeft: theme.spacing(1),
        verticalAlign: 'middle',
    },
    imgidPlaceholder: {
        maxWidth: '250px',
        height: '160px',
    },
    imgid: {
        maxWidth: '250px',
        height: 'auto',
        borderRadius: theme.spacing(0.5),
        boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
    },
    imgidContainer: {
        display: 'flex',
        justifyContent: 'center',
    },
    barcode: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'center',
    },
    actions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
}));

const DonorEditActions = ({ basePath, data }) => {
    const classes = useStyles();
    const userLevel = localStorage.token ? jwt_decode(localStorage.token).level : 0;

    return (
        <Box className={classes.actions}>
            {userLevel > 1 && <EditButton basePath={basePath} record={data} />}
            <ListButton basePath={basePath} />
        </Box>
    );
};


const ShowDetails = () => {
    const record = useRecordContext();
    const classes = useStyles();

    if (!record) return null;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
                <Card className={classes.card}>
                    <CardContent>
                        <div className={classes.imageContainer}>
                            <ImageWithFallback
                                src={record.photo_url}
                                alt="Donor Photo"
                                className={classes.image}
                                placeholderClassName={classes.photoPlaceholder}
                                fallbackLabel="No donor photo"
                                icon={PersonIcon}
                            />
                        </div>
                        <div className={classes.barcode}>
                            <Typography>
                                <span className={classes.fieldLabel}>SSN:</span>
                                {` ${record.ssn}`}
                            </Typography>
                        </div>
                        <div className={classes.barcode}>
                            <Typography>
                                <span className={classes.fieldLabel}>Donor #:</span>
                                {` ${record.id}`}
                            </Typography>
                        </div>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={8}>
                <Card className={classes.card}>
                    <CardContent>
                        <Typography variant="h6" className={classes.sectionTitle}>
                            Donor Information
                        </Typography>
                        <Divider style={{ marginBottom: '1rem' }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Name:</span>
                                    {` ${record.last_name}, ${record.first_name} ${
                                        record.middle_name || ''
                                    }`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Gender:</span>
                                    {` ${record.gender}`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Date of Birth:</span>
                                    {` ${moment(record.dob).format('MM/DD/YYYY')}`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Phone:</span>
                                    {record.don_ph
                                        ? String(record.don_ph)
                                            .replace(/\D/g, "")
                                            .replace(/^1?(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3")
                                        : "(000) 000-0000"
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Address:</span>
                                    {` ${record.apt || ''} ${record.street}, ${
                                        record.city || ''
                                    } ${record.zip || ''} ${record.state || ''}`}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Deferral:</span>

                                    <span
                                        style={{
                                            color:
                                                record.deferral_code === 'ID'
                                                    ? 'orange'
                                                    : record.deferral_code === 'TD'
                                                    ? '#d4a900'
                                                    : record.deferral_code === 'PR'
                                                        ? 'red'
                                                        : 'inherit',
                                            fontWeight: record.deferral_code ? 600 : 'normal',
                                        }}
                                    >
                                        {` ${record.deferral_code || ''}`}
                                    </span>

                                    {record.deferral_date
                                        ? ` ${moment(record.deferral_date).format('MM/DD/YYYY')}`
                                        : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Deferral Expire:</span>
                                    {record.deferral_date
                                        ? ` ${moment(record.deferral_exp_date).format('MM/DD/YYYY')}`
                                        : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Status:</span>
                                    <span
                                        style={{
                                            color:
                                                record.status === 'A'
                                                    ? 'orange'
                                                    : record.status === 'Q'
                                                    ? 'green'
                                                    : 'inherit',
                                            fontWeight: record.status ? 600 : 'normal',
                                        }}
                                    >
                                        {record.status === 'A'
                                            ? ' Applicant'
                                            : record.status === 'Q'
                                            ? ' Qualified'
                                            : ` ${record.status || ''}`}
                                    </span>
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Deferral Comments:</span>
                                    {` ${record.deferral_comments || 'N/A'}`}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography>
                                    <span className={classes.fieldLabel}>NDDR Transaction #:</span>
                                    {record.nddr || "*******" }
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography>
                                    <span className={classes.fieldLabel}>Allergies:</span>
                                    {` ${record.allergies || 'N/A'}`}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography component="div">
                                    <span className={classes.fieldLabel}>Donor Signature:</span>
                                    <ImageWithFallback
                                        src={record.donor_signature}
                                        alt="Donor Signature"
                                        className={classes.sign}
                                        placeholderClassName={classes.signPlaceholder}
                                        fallbackLabel="Not signed"
                                        icon={GestureIcon}
                                    />
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} className={classes.activityGridItem}>
                <Card className={classes.card}>
                    <CardContent className={classes.sectionContent}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                            Donor Activity
                        </Typography>
                        <Divider style={{ marginBottom: '1rem' }} />
                        <DataList
                            heading="History"
                            endpointUrl="donhistorylist"
                            query={{ filter: { ssn: record.ssn }, sort: 'id', order: 'DESC' }}
                            initiallyOpen={false}
                        />
                        <DataList
                            heading="Physicals"
                            endpointUrl="physicalslist"
                            query={{ filter: { ssn: record.ssn }, sort: 'id', order: 'DESC' }}
                            initiallyOpen={false}
                        />
                        <DataList
                            heading="SPE"
                            endpointUrl="spelist"
                            query={{ filter: { ssn: record.ssn }, sort: 'id', order: 'DESC' }}
                            initiallyOpen={false}
                        />
                        <DataList
                            heading="Collections"
                            endpointUrl="collectionlist"
                            query={{ filter: { ssn: record.ssn }, sort: 'bleed_date', order: 'DESC' }}
                            initiallyOpen={false}
                        />
                        {/* Donor reaction / incident reports recorded on the
                            processing worksheet. Sits next to Collections
                            because each one belongs to a donation. */}
                        <DataList
                            heading="Incidents"
                            endpointUrl="donorincidentlist"
                            query={{ filter: { ssn: record.ssn }, sort: 'incident_date', order: 'DESC' }}
                            initiallyOpen={false}
                        />
                        <DataList
                            heading="Logs"
                            endpointUrl="logslist"
                            query={{ filter: { donor_num: record.ssn }, sort: 'id', order: 'DESC' }}
                            initiallyOpen={false}
                        />
                    </CardContent>
                </Card>
            </Grid>
            {(record.photo_id_1_url || record.photo_id_2_url) && (
                <Grid item xs={12}>
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography variant="h6" className={classes.sectionTitle}>
                                Photo IDs
                            </Typography>
                            <Divider style={{ marginBottom: '1rem' }} />
                            <Grid container spacing={2}>
                                {record.photo_id_1_url && (
                                    <Grid item xs={12} sm={6} className={classes.imgidContainer}>
                                        <ImageWithFallback
                                            src={record.photo_id_1_url}
                                            alt="Photo ID 1"
                                            className={classes.imgid}
                                            placeholderClassName={classes.imgidPlaceholder}
                                            fallbackLabel="Photo ID 1 unavailable"
                                            icon={CreditCardIcon}
                                        />
                                    </Grid>
                                )}
                                {record.photo_id_2_url && (
                                    <Grid item xs={12} sm={6} className={classes.imgidContainer}>
                                        <ImageWithFallback
                                            src={record.photo_id_2_url}
                                            alt="Photo ID 2"
                                            className={classes.imgid}
                                            placeholderClassName={classes.imgidPlaceholder}
                                            fallbackLabel="Photo ID 2 unavailable"
                                            icon={CreditCardIcon}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </Grid>
    );
};

const Title = ({ record }) => {
    if (!record) return null;
    return <span>{`${record.last_name}, ${record.first_name}`}</span>;
};

const DonorView = (props) => {
    const classes = useStyles();
    return (
        <div className={classes.root}>
            <ErrorBoundary>
                {/*
                  <Show> already renders its own Card. Wrapping the details in a
                  second Card + CardContent stacked another ~48px of horizontal
                  padding on each side for no visual gain, so the details render
                  directly into the Show card.
                */}
                <Show
                    {...props}
                    actions={<DonorEditActions />}
                    title={<Title />}
                    classes={{ card: classes.showCard }}
                >
                    <ShowDetails />
                </Show>
            </ErrorBoundary>
        </div>
    );
};

export default DonorView;
